import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography
} from '@material-ui/core';
import BusinessIcon from '@material-ui/icons/Business';
import PersonIcon from '@material-ui/icons/Person';
import WorkIcon from '@material-ui/icons/Work';
import {
  APP_PATH_DASHBOARD,
  APP_PATH_MY_APPLICATIONS,
  MAIN_APP_NAME
} from '../../config';
import { useUserTypeSelection } from '../../hooks/useUserTypeSelection';
import { useCurrentUserInfo } from '../../hooks/useCurrentUserInfo';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3)
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(6)
  },
  title: {
    color: theme.palette.text.primary,
    fontWeight: 700,
    marginBottom: theme.spacing(2)
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: '1.25rem',
    maxWidth: 600,
    margin: '0 auto'
  },
  cardsContainer: {
    maxWidth: 900,
    width: '100%'
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
    border: `2px solid ${theme.palette.divider}`,
    transition: 'all 0.3s ease-in-out',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-8px)',
      boxShadow: theme.shadows[10],
      borderColor: theme.palette.primary.main
    }
  },
  iconWrapper: {
    width: 120,
    height: 120,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(3),
    transition: 'all 0.3s ease-in-out'
  },
  companyIconWrapper: {
    backgroundColor: theme.palette.primary.light,
    '&:hover': {
      backgroundColor: theme.palette.primary.main
    }
  },
  candidateIconWrapper: {
    backgroundColor: theme.palette.secondary.light,
    '&:hover': {
      backgroundColor: theme.palette.secondary.main
    }
  },
  icon: {
    fontSize: 60,
    color: theme.palette.common.white
  },
  cardTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2)
  },
  cardDescription: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3)
  },
  button: {
    marginTop: 'auto',
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    fontWeight: 600,
    fontSize: '1rem'
  },
  loadingContainer: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3)
  },
  loadingIcon: {
    marginBottom: theme.spacing(3),
    color: theme.palette.primary.main
  },
  loadingTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary
  },
  loadingSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: '1.1rem'
  },
  workIcon: {
    fontSize: 80,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
    animation: '$pulse 2s ease-in-out infinite'
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(1)',
      opacity: 1
    },
    '50%': {
      transform: 'scale(1.1)',
      opacity: 0.8
    },
    '100%': {
      transform: 'scale(1)',
      opacity: 1
    }
  }
}));

const UserTypeSelection = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useCurrentUserInfo();
  const { selectCompany, selectCandidate, isLoading } = useUserTypeSelection();
  const [isCheckingRole, setIsCheckingRole] = useState(true);
  const roles = user?.roles;
  useEffect(() => {
    // If user already has a company or candidate role, redirect to appropriate page
    if (roles) {
      const isCompany = roles.includes('ROLE_COMPANY');
      const isCandidate = roles.includes('ROLE_CANDIDATE');

      if (isCompany) {
        console.log('👤 User is a company, redirecting to dashboard');
        history.push(APP_PATH_DASHBOARD);
      } else if (isCandidate) {
        console.log('👤 User is a candidate, redirecting to my applications');
        history.push(APP_PATH_MY_APPLICATIONS);
      } else {
        // User has no role yet, show selection page
        setIsCheckingRole(false);
      }
    }
  }, [roles, user, history]);

  const handleCompanyClick = () => {
    if (!isLoading) {
      selectCompany();
    }
  };

  const handleCandidateClick = () => {
    if (!isLoading) {
      selectCandidate();
    }
  };

  // Show loading state while checking user's role
  if (isCheckingRole) {
    return (
      <div className={classes.loadingContainer}>
        <WorkIcon className={classes.workIcon} />
        <Typography variant="h3" className={classes.loadingTitle}>
          Loading Your Workspace
        </Typography>
        <Typography variant="body1" className={classes.loadingSubtitle}>
          Preparing awesome jobs and talented candidates...
        </Typography>
        <Box mt={3}>
          <CircularProgress className={classes.loadingIcon} size={40} />
        </Box>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h1" className={classes.title}>
          Welcome to {MAIN_APP_NAME}
        </Typography>
        <Typography className={classes.subtitle}>
          To provide you with the best experience, please tell us how you'd like
          to use our platform
        </Typography>
      </div>

      <Grid
        container
        spacing={4}
        justifyContent="center"
        className={classes.cardsContainer}>
        <Grid item xs={12} sm={6} md={5}>
          <Card
            className={classes.card}
            onClick={handleCompanyClick}
            elevation={3}
            style={{ opacity: isLoading ? 0.6 : 1 }}>
            <div
              className={`${classes.iconWrapper} ${classes.companyIconWrapper}`}>
              <BusinessIcon className={classes.icon} />
            </div>
            <CardContent style={{ textAlign: 'center' }}>
              <Typography variant="h3" className={classes.cardTitle}>
                I'm a Company
              </Typography>
              <Typography variant="body1" className={classes.cardDescription}>
                Looking to hire talented professionals and build your remote
                team
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="large"
                className={classes.button}
                disabled={isLoading}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCompanyClick();
                }}>
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Continue as Company'
                )}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={5}>
          <Card
            className={classes.card}
            onClick={handleCandidateClick}
            elevation={3}
            style={{ opacity: isLoading ? 0.6 : 1 }}>
            <div
              className={`${classes.iconWrapper} ${classes.candidateIconWrapper}`}>
              <PersonIcon className={classes.icon} />
            </div>
            <CardContent style={{ textAlign: 'center' }}>
              <Typography variant="h3" className={classes.cardTitle}>
                I'm a Candidate
              </Typography>
              <Typography variant="body1" className={classes.cardDescription}>
                Searching for remote job opportunities and career growth
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                className={classes.button}
                disabled={isLoading}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCandidateClick();
                }}>
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Continue as Candidate'
                )}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default UserTypeSelection;
